import { useContext, useState, useEffect } from "react";

import { Button, IconButton, FormControlLabel, } from "@material-ui/core";
import clsx from "clsx";
import { useFormik } from "formik";
import * as Yup from "yup";
import { withUbdAnalyticsStep } from "@aspirasi/react-data-analytics";

import { StoreContext } from "../../../store/context";

import { ValidationUtil } from "../../../utils/validation";
import TrackingUtil from "../../../utils/tracking";
import { formatLabel } from "../../../utils";

import { NC_EVT } from "../../../constants/tracking";
import { EPF_CONTRIBUTION_TYPE, RACE_TYPE } from "../../../constants/form";
import { APPLICATION_STATUS } from "../../../constants/applicationStatus";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import CustomCheckboxTerms from "../../../components/CustomCheckboxTerms";
import FormikForm from "../../../components/form/FormikForm";
import ExperianBanner, { useExperianBanner } from "../../../components/ExperianBanner";

import { useThemeStyles } from "../../../styles/themes";
import "./StepAdditionalDetails.scss";
import { TRACKING_PROVIDER } from "../../../config/tracking/providers";
import adapters from "../../../config/tracking/adapters";

const validationSchema = Yup.object().shape({
  maritalStatus: Yup.string().nullable().required("This is a required field"),
  race: Yup.string().nullable().required("This is a required field"),
  employerName: Yup.string().required("This is a required field.").matches(ValidationUtil.REGEX_VALID_FULLNAME, "Invalid Employer Name"),
  ethnicity: Yup.string().required("This is a required field.").oneOf(["BUMIPUTERA", "NON_BUMIPUTERA"], "Please select a valid option"),
  epfContribution: Yup.string().required("This is a required field."),
  pacInd: Yup.bool().oneOf([true], "Please accept the form"),
  tncInd: Yup.bool().oneOf([true], "Please accept the form"),
  pepInd: Yup.bool().oneOf([true], "Please accept the form"),
  isConsent: Yup.bool().oneOf([true], "Please accept the form"),
});

const StepAdditionalDetails = (props) => {
  const { stepText, analyticsStep } = props;

  const classes = useThemeStyles();
  const { state, actions } = useContext(StoreContext);
  const { isOnboardingUpdate } = state;
  const experianBannerProps = useExperianBanner();

  const tncUrl = isOnboardingUpdate ? state.prerequisites?.merchant?.termAndConditionConvUrl : state.prerequisites?.merchant?.termAndConditionUrl;
  const privacyNoticeUrl = isOnboardingUpdate ? state.prerequisites?.merchant?.privacyNoticeConvUrl : state.prerequisites?.merchant?.privacyNoticeUrl;
  const keyTermsUrl = isOnboardingUpdate ? state.prerequisites?.merchant?.keyTermsConvUrl : null;
  const pdsUrl = isOnboardingUpdate ? state.prerequisites?.merchant?.productDisclosureSheetConvUrl : null;

  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const lang = "en";

  const initialValues = {
    applicationId: state.applicationId,
    maritalStatus: "",
    race: "",
    employerName: "",
    ethnicity: "",
    epfContribution: "",
    tncInd: false,
    pepInd: false,
    pacInd: false,
    isConsent: false,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      TrackingUtil.sendNetcoreEventHelper(NC_EVT.ACT_ADDITIONAL_DETAILS_SUBMIT);

      const data = {
        ...values,
        ...(isOnboardingUpdate && { changeFunderAction: APPLICATION_STATUS.ACCEPTED })
      };

      TrackingUtil.pushUserDataForProvider(TRACKING_PROVIDER.NETCORE, {
        customerId: state.externalCustomerId, // Using life side customer id as application id
        marital_status: values.maritalStatus,
        race: values.race,
        bumiputera_status: formatLabel(values.ethnicity),
        employer_name: values.employerName,
        epf_contribution: values.epfContribution === EPF_CONTRIBUTION_TYPE.NO_ACTIVE_CONTRIBUTION.value ? "no" : "yes",
      });

      // AnalyticsStep: End step
      analyticsStep.handleStepEnd();

      // Dismiss Experian BANNER after Proceed (per Figma)
      if (state.experian) {
        actions.setData({ experian: null });
      }

      actions.updateOnboarding(data);
    },
  });

  const handleSelectAll = async (event) => {
    const { checked } = event.target;
    const terms = ["tncInd", "pepInd", "pacInd", "isConsent"];

    setSelectAllChecked(checked);

    // async value
    await formik.setValues({
      ...formik.values,
      // selectAll: checked,
      tncInd: checked,
      pepInd: checked,
      pacInd: checked,
      isConsent: checked
    });

    // manually mark fields as touched for validation
    formik.setTouched(
      {
        ...formik.touched,
        ...Object.fromEntries(terms.map((term) => [term, true])),
      },
      false
    );

    // manually mark fields for validation
    terms.forEach((term) => formik.validateField(term));
  };

  // handle onBlur individually to avoid validateOnBlur validating all fields
  const handleFormikBlur = (fieldName) => async (e) => {
    await formik?.handleBlur(e);
    formik?.validateField(fieldName);
  };

  const handleFormikChange = (fieldName) => async (e) => {
    await formik?.handleChange(e);
    formik?.validateField(fieldName);
  };

  const isSubmitEnable = () => {
    // Ensure applicationId is touched.
    // Prevents submit button from staying disabled after fail.
    if (!formik.touched?.applicationId) {
      formik.setTouched({ ...formik.touched, applicationId: true });
    }

    // const { applicationId, ...rest } = initialValues;
    const requiredFieldCount = Object.keys(initialValues).length;
    const touchedFieldCount = Object.keys(formik.touched).length;
    const isAllFieldsTouched = requiredFieldCount === touchedFieldCount;

    return isAllFieldsTouched && formik.isValid && !formik.isValidating;
  };

  // Auto-populate fields for users returning after Experian downtime
  useEffect(() => {
    const prefillData = state.piiData?.additionalDetails;
    if (!prefillData || Object.keys(prefillData).length === 0) return;

    const newValues = {};
    if (prefillData.maritalStatus) newValues.maritalStatus = prefillData.maritalStatus;
    if (prefillData.race) newValues.race = prefillData.race;
    if (prefillData.bumiputeraStatus) newValues.ethnicity = prefillData.bumiputeraStatus;
    if (prefillData.employerName) newValues.employerName = prefillData.employerName;
    if (prefillData.epfContribution) newValues.epfContribution = prefillData.epfContribution;

    formik.setValues((prev) => ({ ...prev, ...newValues }));
    formik.setTouched({ ...formik.touched, ...Object.fromEntries(Object.keys(newValues).map((k) => [k, true])) }, false);
  }, [state.piiData?.additionalDetails]);

  useEffect(() => {
    TrackingUtil.sendNetcoreEventHelper(NC_EVT.VIEW_ADDITIONAL_DETAILS_PAGE);
  }, []);

  return (
    state.prerequisites
    && (
      <>
        <Header />
        <div
          className="page page-additional-details"
          style={experianBannerProps ? { overflow: "hidden" } : undefined}
        >
          <div className={`${classes.content} ${classes.customScrollbar} content custom-scrollbar`}>
            {experianBannerProps
              ? <ExperianBanner />
              : (
                <IconButton className="btn-back">
                  <img alt="back" src="/images/rounded-left-arrow.svg" />
                </IconButton>
              )}

            {stepText && (
              <div className="stepper-holder">
                <span className={`${classes.stepText} step-text`}>
                  Personal Info
                </span>
                {/* { state.prerequisites?.themingConfigurations?.asset?.lockIconUrl && <img className="step-icon" alt="lock" src={state.prerequisites?.themingConfigurations?.asset.lockIconUrl} />} */}
                <img className="step-icon" alt="lock" src="https://aspirasi-static.s3.ap-southeast-1.amazonaws.com/paylater/assets/20210817/icon/0cb37674-336d-4f5d-a77d-2af6a74ad0ea/lock_icon_red.svg" />
              </div>
            )}

            <div>
              <p className={`${classes.title} title`}>Additional Details</p>
              <p className={`${classes.subtitle} sub-title`}>We just need a little more information from you!</p>
              <div className={`${classes.divider} divider`} />
            </div>

            <FormikForm formik={formik}>
              <FormikForm.Select
                label="Marital Status:"
                name="maritalStatus"
                onChange={handleFormikChange("maritalStatus")}
                // onBlur={handleFormikBlur("maritalStatus")}
                options={state.prerequisites?.riskProfile.maritalStatuses.map((maritalStatus, i) => ({
                  label: maritalStatus.value[lang],
                  value: maritalStatus.value[lang],
                  // value: maritalStatus.maritalStatusId
                }))}
              />

              <FormikForm.Select
                label="Race:"
                name="race"
                onChange={handleFormikChange("race")}
                // onBlur={handleFormikBlur("race")}
                options={Object.values(RACE_TYPE).map((race) => ({ label: race, value: race }))}
              />

              <FormikForm.Select
                label="Bumiputera Status:"
                name="ethnicity"
                onChange={handleFormikChange("ethnicity")}
                // onBlur={handleFormikBlur("ethnicity")}
                options={[
                  { label: "Bumiputera", value: "BUMIPUTERA" },
                  { label: "Non-Bumiputera", value: "NON_BUMIPUTERA" }
                ]}
              />

              <FormikForm.Input
                label="Employer Name:"
                name="employerName"
                onChange={handleFormikChange("employerName")}
                // onBlur={handleFormikBlur("employerName")}
              />

              <FormikForm.Select
                label="EPF Contribution:"
                name="epfContribution"
                onChange={handleFormikChange("epfContribution")}
                // onBlur={handleFormikBlur("epfContribution")}
                options={Object.values(EPF_CONTRIBUTION_TYPE)?.map((item) => ({ label: item.label, value: item.value }))}
              />

              <div>
                <div className={`${classes.divider} divider`} />

                <p className="note">An account will be created for you based on your verified information with Boost.</p>
              </div>

              <FormControlLabel
                className={clsx("terms-holder", "terms-select-all", { "terms-checked": selectAllChecked }, (selectAllChecked ? classes.inputCheckBoxActive : classes.inputCheckBoxDefault))}
                control={(
                  <CustomCheckboxTerms
                    newStyle
                    color="primary"
                    className="terms-cb"
                    name="selectAll"
                    checked={formik.values.tncInd && formik.values.pacInd && formik.values.pepInd && formik.values.isConsent}
                    onChange={handleSelectAll}
                  />
                )}
                label={(
                  <p className="terms">
                    Select All
                  </p>
                )}
              />

              <FormControlLabel
                className={clsx("terms-holder", { "terms-checked": formik.values.tncInd }, (formik.values.tncInd ? classes.inputCheckBoxActive : classes.inputCheckBoxDefault))}
                control={(
                  <CustomCheckboxTerms
                    newStyle
                    color="primary"
                    className="terms-cb"
                    name="tncInd"
                    checked={formik.values.tncInd}
                    // onChange={formik.handleChange}
                    onChange={handleFormikChange("tncInd")}
                    // onBlur={handleFormikBlur("tncInd")}
                  />
                )}
                label={(
                  <p className="terms">
                    I confirm that I have read, understand and agree to the
                    {" "}
                    {" "}
                    <a target="_blank" rel="noopener noreferrer" href={tncUrl}>Terms & Conditions</a>
                    {isOnboardingUpdate && (
                      <>

                        {", "}
                        <a target="_blank" rel="noopener noreferrer" href={keyTermsUrl}>Key Terms</a>,
                        {" "}
                        <a target="_blank" rel="noopener noreferrer" href={pdsUrl}>Product Disclosure Sheet</a>
                      </>
                    )}
                    {" "}
                    and
                    {" "}
                    <a target="_blank" rel="noopener noreferrer" href={privacyNoticeUrl}>Privacy Notice</a>
                    {" "}
                    of Boost and Boost’s financing partners.
                  </p>
                )}
              />

              <FormControlLabel
                className={clsx("terms-holder", { "terms-checked": formik.values.pepInd }, (formik.values.pepInd ? classes.inputCheckBoxActive : classes.inputCheckBoxDefault))}
                control={(
                  <CustomCheckboxTerms
                    newStyle
                    color="primary"
                    className="terms-cb"
                    name="pepInd"
                    checked={formik.values.pepInd}
                    // onChange={formik.handleChange}
                    onChange={handleFormikChange("pepInd")}
                    // onBlur={handleFormikBlur("pepInd")}
                  />
                )}
                label={(
                  <p className="terms">
                    I confirm that I am not a close associate of or a Politically Exposed Person (PEP).
                  </p>
                )}
              />

              <FormControlLabel
                className={clsx("terms-holder", { "terms-checked": formik.values.pacInd }, (formik.values.pacInd ? classes.inputCheckBoxActive : classes.inputCheckBoxDefault))}
                control={(
                  <CustomCheckboxTerms
                    newStyle
                    color="primary"
                    className="terms-cb"
                    name="pacInd"
                    checked={formik.values.pacInd}
                    // onChange={formik.handleChange}
                    onChange={handleFormikChange("pacInd")}
                    // onBlur={handleFormikBlur("pacInd")}
                  />
                )}
                label={(
                  <p className="terms">
                    I consent to Boost and its financing partners of using my personal data, including data received from my financing provider for marketing and analytic purposes outside of PayFlex. For more details, please refer to the
                    {" "}
                    <a target="_blank" rel="noopener noreferrer" href={state.prerequisites?.merchant?.privacyNoticeUrl}>Privacy Notice</a>
                    {" "}
                    . To withdraw your consent, you can write to <a className="contact-link" href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</a>.
                  </p>
                )}
              />

              <div className="action-holder">
                <Button
                  disabled={!isSubmitEnable()}
                  className={`${classes.btnContained} btn-next`}
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Proceed
                </Button>
              </div>

            </FormikForm>
          </div>
          <Footer />
        </div>
      </>
    )
  );
};

export default withUbdAnalyticsStep(StepAdditionalDetails, {
  stepId: "StepAdditionalDetails",
  capturingFieldNames: [
    "maritalStatus",
    "race",
    "employerName",
    "ethnicity",
    "epfContribution",
    "tncInd",
    "pepInd",
    "pacInd",
    "isConsent"
  ]
});
